import {
    ColumnDefinition,
    ColumnDefinitionsDictionary,
    getColDefsForColumnDefinitionsDictionary,
    ValueType,
} from '@instinet-analytics/semantic-ui-components';
import { IndigoAppContextType } from 'app/AppContext';
import { setResultCellRenderer } from 'widgets/estimatedTradeCost/widget-comparison/components/grid/renderers/resultCellRendererSelector';
import {
    endTimeHelp,
    maxPartLitHelp,
    minPartLitHelp,
    riskHelp,
    startTimeHelp,
    totalCostHelp,
} from 'widgets/estimatedTradeCost/widget/components/help/property-help';

export type EstimatedTradeCostComparisonColumn =
    | 'benchmark'
    | 'profile'
    | 'minPartLit'
    | 'maxPartLit'
    | 'strikeTime'
    | 'totalCost'
    | 'totalRisk'
    | 'efPart'
    | 'startTime'
    | 'endTime';

export const estimatedTradeCostComparisonColumnsDefinitionDictionary: ColumnDefinitionsDictionary<EstimatedTradeCostComparisonColumn> =
    {
        benchmark: {
            field: 'benchmark',
            name: 'Benchmark',
            headerTooltip: 'Benchmark',
            headerText: 'BM',
            valueType: ValueType.STRING,
            tag: 'Request',
        },
        profile: {
            field: 'profile',
            name: 'Profile',
            headerText: 'PR',
            headerTooltip: 'Profile',
            valueType: ValueType.STRING,
            tag: 'Request',
        },
        minPartLit: {
            field: 'minPartLit',
            name: 'Min Part Lit',
            valueType: ValueType.NUMBER,
            precision: 0,
            isPercentage: true,
            tag: 'Request',
            headerTooltip: minPartLitHelp,
        },
        maxPartLit: {
            field: 'maxPartLit',
            name: 'Max Part Lit',
            valueType: ValueType.NUMBER,
            precision: 0,
            isPercentage: true,
            headerTooltip: maxPartLitHelp,
            tag: 'Request',
        },
        strikeTime: {
            field: 'strikeTime',
            name: 'Strike Time',
            valueType: ValueType.STRING,
            tag: 'Request',
        },
        totalCost: {
            field: 'totalCost',
            name: 'Cost',
            valueType: ValueType.NUMBER,
            precision: 2,
            headerTooltip: totalCostHelp,
            tag: 'Response',
        },
        totalRisk: {
            field: 'totalRisk',
            name: 'Risk',
            valueType: ValueType.NUMBER,
            precision: 2,
            headerTooltip: riskHelp,
            tag: 'Response',
        },
        efPart: {
            field: 'efPart',
            name: 'Participation',
            valueType: ValueType.NUMBER,
            precision: 2,
            isPercentage: true,
            tag: 'Response',
            headerTooltip: 'Effective Participation',
        },
        startTime: {
            field: 'startTime',
            name: 'Start Time',
            valueType: ValueType.STRING,
            tag: 'Response',
            headerTooltip: startTimeHelp,
        },
        endTime: {
            field: 'endTime',
            name: 'End Time',
            valueType: ValueType.STRING,
            tag: 'Response',
            headerTooltip: endTimeHelp,
        },
    };

export const estimatedTradeCostComparisonColumnsDefinitions: ColumnDefinition[] =
    Object.values(estimatedTradeCostComparisonColumnsDefinitionDictionary);

export function getEstimatedTradeCostComparisonColDefs(
    context: IndigoAppContextType,
    onClickRefresh: () => void
) {
    const colDefs = getColDefsForColumnDefinitionsDictionary(
        context,
        estimatedTradeCostComparisonColumnsDefinitionDictionary
    );

    const setCellRenderer = (field: string) => {
        const colDef = colDefs.find((c) => c.field === field)!;
        colDef.cellRenderer = field;
        colDef.tooltipComponentParams.showAutoCellTooltip = false;
    };

    setCellRenderer('benchmark');
    setCellRenderer('profile');

    setResultCellRenderer(colDefs, 'totalCost', onClickRefresh);
    setResultCellRenderer(colDefs, 'totalRisk', onClickRefresh);

    return colDefs;
}
